import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { Card, PageHeader, Btn, EmptyState } from "../components/UI";
import { TrackCandidateModal } from "./intelligence/TrackCandidateModal";
import { toast } from "sonner";
import { BrainCircuit, Plus, Trash2, TrendingUp, TrendingDown, BarChart3, Loader2, Landmark } from "lucide-react";

export const partyPill = (p) =>
  p === "Democratic" ? "text-sky-300 border-sky-400/30 bg-sky-400/10"
    : p === "Republican" ? "text-rose-300 border-rose-400/30 bg-rose-400/10"
    : "text-amber-300 border-amber-400/30 bg-amber-400/10";

export default function PoliticalIntelligence() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const load = useCallback(() => {
    api.get("/political-intelligence/candidates").then(r => setRows(r.data)).catch(() => setRows([]));
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (e, row) => {
    e.stopPropagation();
    if (!window.confirm(`Stop tracking ${row.candidate_name}? All polls, issues and strategies for this race will be deleted.`)) return;
    try {
      await api.delete(`/political-intelligence/candidates/${row.id}`);
      toast.success("Candidate removed");
      load();
    } catch (err) { toast.error(errMsg(err, "Could not remove candidate")); }
  };

  if (!rows) return <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2 text-cyan-300" />Loading intelligence…</div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="political-intelligence-page">
      <PageHeader
        eyebrow="TotalReach Political Intelligence™"
        title="Know the Race"
        subtitle="Track your candidate and opponents — polling averages, issue intelligence, AI strategy scenarios and honest before-vs-after measurement."
        action={
          <Btn onClick={() => setModalOpen(true)} data-testid="track-candidate-btn">
            <Plus className="w-4 h-4" /> Track a candidate
          </Btn>
        }
      />

      {rows.length === 0 ? (
        <Card>
          <EmptyState icon={BrainCircuit} title="No tracked races yet"
            description="Pick your candidate from the live FEC roster (2,500+ declared 2026 candidates) or add a custom/local candidate — then log polls, track issues and generate AI strategy scenarios."
            action={<Btn onClick={() => setModalOpen(true)} data-testid="empty-track-candidate-btn"><Plus className="w-4 h-4" /> Track your first candidate</Btn>} />
        </Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {rows.map((r) => (
            <Card key={r.id} className="cursor-pointer hover:border-cyan-400/40 transition-colors group"
              onClick={() => navigate(`/political-intelligence/${r.id}`)} data-testid={`pi-candidate-card-${r.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-display font-bold text-lg truncate" data-testid="pi-card-name">{r.candidate_name}</div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full border ${partyPill(r.party)}`}>{r.party}</span>
                    <span className="text-xs text-slate-400 inline-flex items-center gap-1">
                      {r.source === "fec" ? <Landmark className="w-3 h-3 text-cyan-300/70" /> : null}
                      {r.office || "Race"} · {r.state}{r.district && r.district !== "00" ? `-${r.district}` : ""}
                    </span>
                  </div>
                </div>
                <button onClick={(e) => remove(e, r)} data-testid={`pi-delete-${r.id}`}
                  className="opacity-0 group-hover:opacity-100 text-slate-500 hover:text-rose-300 transition-all shrink-0 p-1">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-white/[0.03] border border-white/5 py-2">
                  <div className="font-display text-xl font-bold text-cyan-300">{r.summary?.my_avg != null ? `${r.summary.my_avg}%` : "—"}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Poll avg</div>
                </div>
                <div className="rounded-lg bg-white/[0.03] border border-white/5 py-2">
                  <div className={`font-display text-xl font-bold inline-flex items-center gap-1 ${r.summary?.margin > 0 ? "text-emerald-300" : r.summary?.margin < 0 ? "text-rose-300" : "text-slate-300"}`}>
                    {r.summary?.margin != null ? (<>{r.summary.margin > 0 ? <TrendingUp className="w-4 h-4" /> : r.summary.margin < 0 ? <TrendingDown className="w-4 h-4" /> : null}{r.summary.margin > 0 ? "+" : ""}{r.summary.margin}</>) : "—"}
                  </div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Margin</div>
                </div>
                <div className="rounded-lg bg-white/[0.03] border border-white/5 py-2">
                  <div className="font-display text-xl font-bold text-white">{r.summary?.poll_count ?? 0}</div>
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Polls</div>
                </div>
              </div>
              {r.opponents?.length > 0 && (
                <div className="mt-3 text-xs text-slate-500 truncate">
                  vs {r.opponents.map(o => o.name).join(", ")}
                </div>
              )}
            </Card>
          ))}
        </div>
      )}

      {modalOpen && (
        <TrackCandidateModal
          onClose={() => setModalOpen(false)}
          onCreated={(row) => { setModalOpen(false); navigate(`/political-intelligence/${row.id}`); }}
        />
      )}
    </div>
  );
}
